import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import React, { useEffect, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Button, Dialog, Portal, Searchbar } from 'react-native-paper';
import {
  LabelChip,
  LabelItem,
  LabelTypePicker,
} from 'shared/components/WorkoutEdit/LabelsInput';
import useConfirmModal from 'shared/hooks/useConfirmModal';
import useDebouncedState from 'shared/hooks/useDebounceState';
import useOrientation from 'shared/hooks/useOrientation';
import useSnackBar from 'shared/hooks/useSnackBar';
import { sizes, vh, vw } from 'shared/styles';
import { Label, LabelType, Track } from 'shared/types';
import { pluralize } from 'shared/utils';
import { useImmer } from 'use-immer';

interface Props {
  workoutIds: string[];
  track: Track;
  workoutLabels: Label[];
  onDismiss: () => void;
  onSuccess: () => void;
}

const BULK_MUTATION = gql`
  mutation BulkUpdateWorkouts($input: WorkoutBulkUpdateInput!) {
    workoutBulkUpdate(input: $input) {
      id
      labels {
        id
        name
        type
      }
    }
  }
`;

const WorkoutsBulkLabels = ({
  onDismiss,
  workoutIds,
  track,
  workoutLabels,
  onSuccess,
}: Props) => {
  const confirmWith = useConfirmModal();
  const showSnackBar = useSnackBar();
  const { isPortrait } = useOrientation();
  const [labels, updateLabels] = useImmer<Label[]>(workoutLabels);
  const [search, setSearch, unbouncedSearch] = useDebouncedState('');
  const [labelType, setLabelType] = useState<LabelType | undefined>();
  const [filteredLabels, setFilteredLabels] = useState(track.labels || []);

  const [bulkChangeWorkouts, { loading }] = useMutation(BULK_MUTATION);

  useEffect(() => {
    let filtered = track.labels || [];

    if (!!search) {
      filtered = filtered.filter((l) =>
        l.name.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (labelType) {
      filtered = filtered.filter((l) => l.type === labelType);
    }

    setFilteredLabels(filtered);
  }, [search, labelType, track]);

  const displayError = (message: string) => {
    confirmWith({ title: 'Error', message, hideCancel: true });
  };

  const onSave = () => {
    bulkChangeWorkouts({
      variables: {
        input: {
          ids: workoutIds,
          labels: labels.map((l) => ({ name: l.name, type: l.type })),
        },
      },
    })
      .then(() => {
        onSuccess();
        showSnackBar({
          message: `Successfully updated ${workoutIds.length} ${pluralize(workoutIds.length, 'workout', 'workouts')}`,
        });
      })
      .catch(({ graphQLErrors }) => {
        const message =
          graphQLErrors && graphQLErrors.map((e: any) => e.message).join(', ');
        displayError(message || 'Error updating workouts. Please, try again!');
      });
  };

  return (
    <Portal>
      <Dialog
        visible={true}
        onDismiss={onDismiss}
        style={[{ height: isPortrait ? vh(60) : vw(60) }, styles.dialog]}
      >
        <Dialog.Title>
          Update Labels for {workoutIds.length}{' '}
          {pluralize(workoutIds.length, 'Workout', 'Workouts')}
        </Dialog.Title>
        <Dialog.Content>
          <ScrollView
            contentContainerStyle={{
              paddingHorizontal: sizes.spacing,
              marginBottom: labels.length > 0 ? sizes.spacing : 0,
            }}
            horizontal
            showsHorizontalScrollIndicator={false}
          >
            {labels.map((label, index) => (
              <LabelChip
                label={label}
                key={`${index}-${label.name}`}
                onChangeType={(type) =>
                  updateLabels((draft) => {
                    draft[index].type = type;
                  })
                }
                onClose={() => {
                  confirmWith({
                    message: `Are you sure you want to remove '${label.name}'?`,
                    confirmLabel: 'Remove',
                    onConfirm: () => {
                      updateLabels((draft) => {
                        draft.splice(index, 1);
                      });
                    },
                  });
                }}
              />
            ))}
          </ScrollView>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LabelTypePicker
              labelType={labelType}
              setLabelType={setLabelType}
            />

            <Searchbar
              style={{ height: 40, flex: 1 }}
              placeholder="Type label name"
              value={unbouncedSearch}
              onChangeText={setSearch}
            />
          </View>
        </Dialog.Content>
        <Dialog.ScrollArea>
          <ScrollView>
            {!!search && (
              <LabelItem
                label={{ name: search, type: labelType || LabelType.other }}
                labels={labels}
                updateLabels={updateLabels}
              />
            )}
            {filteredLabels.map((label) => (
              <LabelItem
                key={label.id}
                label={label}
                labels={labels}
                updateLabels={updateLabels}
              />
            ))}
          </ScrollView>
        </Dialog.ScrollArea>
        <Dialog.Actions style={styles.actionsContainer}>
          <Button onPress={onDismiss}>Cancel</Button>
          <Button
            mode="contained"
            onPress={onSave}
            loading={loading}
            icon="content-save"
            disabled={loading}
          >
            Save
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
};

const styles = StyleSheet.create({
  dialog: Platform.select({
    default: {},
    web: {
      alignSelf: 'center',
      width: 500,
    },
  }),
  labelContainer: {
    flexDirection: 'row',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(0,0,0,0.29)',
    borderRadius: 16,
    height: 32,
    marginRight: 4,
    paddingRight: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 6,
    justifyContent: 'center',
  },
  actionsContainer: {
    paddingVertical: sizes.spacing,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
});

export default WorkoutsBulkLabels;
